// /pinned — Remote-pin shortcut queue.
// Parent side: review pending pins, accept or dismiss.
// Child side: pin form (compose + send) — only when ?compose=1 query param is set.

import type { RouteRenderer, FamilyContact } from "../types.ts";
import {
  listPending,
  listAccepted,
  acceptPin,
  dismissPin,
  enqueuePin,
  subscribeToPinChanges,
  removePin,
  type PinShortcut,
} from "../chassis/elder/remote-pin.ts";
import { loadState as loadOnboardingState } from "../chassis/elder/onboarding.ts";
import { getSelectedLang, makeBilingual, screenReaderAnnounce } from "../chassis/elder/index.ts";
import { i18n } from "../chassis/elder/i18n-strings.ts";
import { navigate } from "../router.ts";

export const renderPinned: RouteRenderer = (host, params) => {
  const lang = getSelectedLang();
  const composeMode = params.get("compose") === "1";

  const shell = document.createElement("div");
  shell.className = "sarathi-route-shell";

  const headerEl = document.createElement("header");
  const back = document.createElement("button");
  back.type = "button";
  back.style.cssText = "min-width:88px;min-height:88px;font-size:32px;background:transparent;border:3px solid var(--sarathi-ink);border-radius:14px;cursor:pointer;font-weight:700";
  back.textContent = "←";
  back.addEventListener("click", () => navigate("home"));
  const headingWrap = document.createElement("div");
  const h1 = document.createElement("h1");
  h1.textContent = composeMode ? "नया बटन भेजें" : "अनुरोध";
  const sub = document.createElement("span");
  sub.className = "secondary";
  sub.textContent = composeMode ? "Send a new home-screen shortcut to your parent" : "Pinned shortcuts and pending requests";
  headingWrap.appendChild(h1);
  headingWrap.appendChild(sub);
  headerEl.appendChild(back);
  headerEl.appendChild(headingWrap);
  shell.appendChild(headerEl);

  let renderBodyFn: () => void = () => undefined;

  const body = document.createElement("div");
  shell.appendChild(body);

  if (composeMode) {
    body.appendChild(renderComposeForm(lang));
  } else {
    renderBodyFn = (): void => {
      body.innerHTML = "";
      body.appendChild(renderPendingSection(lang));
      body.appendChild(renderAcceptedSection(lang));
      body.appendChild(renderComposeShortcut(lang));
    };
    renderBodyFn();
  }

  host.appendChild(shell);
  const unsubscribe = composeMode ? () => undefined : subscribeToPinChanges(() => renderBodyFn());

  return () => {
    unsubscribe();
    shell.remove();
  };
};

function renderPendingSection(lang: ReturnType<typeof getSelectedLang>): HTMLElement {
  const card = document.createElement("section");
  card.className = "sarathi-card";
  card.innerHTML = `<h2>नए अनुरोध</h2><span class="sarathi-secondary-label">New requests</span>`;
  const pending = listPending();
  if (pending.length === 0) {
    const empty = document.createElement("p");
    empty.style.cssText = "margin:14px 0;color:var(--sarathi-ash);font-size:18px";
    empty.textContent = "कोई नया अनुरोध नहीं · No pending requests";
    card.appendChild(empty);
    return card;
  }
  const list = document.createElement("ul");
  list.className = "sarathi-list";
  list.style.marginTop = "14px";
  for (const p of pending) {
    list.appendChild(renderPendingItem(p, lang));
  }
  card.appendChild(list);
  return card;
}

function renderPendingItem(p: PinShortcut, lang: ReturnType<typeof getSelectedLang>): HTMLElement {
  const li = document.createElement("li");
  li.style.flexDirection = "column";
  li.style.alignItems = "stretch";
  li.style.gap = "16px";

  const head = document.createElement("div");
  head.style.cssText = "display:flex;align-items:center;gap:12px";
  const glyph = document.createElement("span");
  glyph.style.cssText = "font-size:38px;line-height:1";
  glyph.setAttribute("aria-hidden", "true");
  glyph.textContent = p.icon || "★";
  const text = document.createElement("div");
  text.innerHTML = `<div style="font-size:22px;font-weight:700">${escapeHtml(p.label)}</div><div style="font-size:16px;color:var(--sarathi-ash)">${escapeHtml(p.fromLabel)} ${escapeHtml(makeBilingual(i18n, "pin.new_request", lang).primary)}</div>`;
  head.appendChild(glyph);
  head.appendChild(text);

  const row = document.createElement("div");
  row.className = "sarathi-row";
  const accept = document.createElement("button");
  accept.type = "button";
  accept.style.cssText = "min-height:72px;background:var(--sarathi-saffron);color:#fff;border:3px solid var(--sarathi-saffron-dark);border-radius:14px;font-size:20px;font-weight:700;cursor:pointer";
  accept.textContent = makeBilingual(i18n, "pin.accept", lang).primary;
  accept.addEventListener("click", () => {
    acceptPin(p.id);
    screenReaderAnnounce(`Shortcut ${p.label} accepted`, { polite: true, clearAfterMs: 3500 });
  });
  const deny = document.createElement("button");
  deny.type = "button";
  deny.style.cssText = "min-height:72px;background:transparent;color:var(--sarathi-ink);border:3px solid var(--sarathi-ink);border-radius:14px;font-size:20px;font-weight:700;cursor:pointer";
  deny.textContent = makeBilingual(i18n, "pin.dismiss", lang).primary;
  deny.addEventListener("click", () => {
    dismissPin(p.id);
    screenReaderAnnounce(`Shortcut dismissed`, { polite: true, clearAfterMs: 3500 });
  });
  row.appendChild(accept);
  row.appendChild(deny);

  li.appendChild(head);
  li.appendChild(row);
  return li;
}

function renderAcceptedSection(_lang: ReturnType<typeof getSelectedLang>): HTMLElement {
  const card = document.createElement("section");
  card.className = "sarathi-card";
  card.innerHTML = `<h2>घर के बटन</h2><span class="sarathi-secondary-label">Active home shortcuts</span>`;
  const accepted = listAccepted();
  if (accepted.length === 0) {
    const empty = document.createElement("p");
    empty.style.cssText = "margin:14px 0;color:var(--sarathi-ash);font-size:18px";
    empty.textContent = "अभी कोई बटन नहीं · No active shortcuts yet";
    card.appendChild(empty);
    return card;
  }
  const list = document.createElement("ul");
  list.className = "sarathi-list";
  list.style.marginTop = "14px";
  for (const p of accepted) {
    const li = document.createElement("li");
    const left = document.createElement("div");
    left.style.cssText = "display:flex;align-items:center;gap:14px";
    left.innerHTML = `<span style="font-size:32px" aria-hidden="true">${escapeHtml(p.icon)}</span><div><div style="font-size:20px;font-weight:700">${escapeHtml(p.label)}</div><div style="font-size:15px;color:var(--sarathi-ash)">From ${escapeHtml(p.fromLabel)}</div></div>`;
    const remove = document.createElement("button");
    remove.type = "button";
    remove.style.cssText = "min-height:60px;padding:0 20px;background:transparent;color:var(--sarathi-danger);border:2px solid var(--sarathi-danger);border-radius:12px;font-size:16px;font-weight:700;cursor:pointer";
    remove.textContent = "हटाएँ · Remove";
    remove.addEventListener("click", () => removePin(p.id));
    li.appendChild(left);
    li.appendChild(remove);
    list.appendChild(li);
  }
  card.appendChild(list);
  return card;
}

function renderComposeShortcut(_lang: ReturnType<typeof getSelectedLang>): HTMLElement {
  const card = document.createElement("section");
  card.className = "sarathi-card";
  card.style.cssText += "text-align:center;background:var(--sarathi-cream-2);border-color:var(--sarathi-saffron)";
  card.innerHTML = `
    <h2>क्या आप बच्चे हैं?</h2>
    <span class="sarathi-secondary-label">Are you the child / family helper?</span>
    <p style="margin:14px 0 18px;font-size:17px;color:var(--sarathi-ink)">Send a new home-screen shortcut to your parent's Sarathi.</p>
    <a href="#/pinned?compose=1" style="display:inline-block;min-height:72px;padding:18px 32px;background:var(--sarathi-saffron);color:#fff;border:3px solid var(--sarathi-saffron-dark);border-radius:14px;font-size:20px;font-weight:700;text-decoration:none">Compose a pin</a>
  `;
  return card;
}

function renderComposeForm(lang: ReturnType<typeof getSelectedLang>): HTMLElement {
  const card = document.createElement("section");
  card.className = "sarathi-card";
  const onboarding = loadOnboardingState();
  const family: readonly FamilyContact[] = onboarding.familyContacts;
  const senderOptions = family.length > 0
    ? family.map((f) => `<option value="${escapeHtml(f.phone)}">${escapeHtml(f.name)}</option>`).join("")
    : `<option value="self">Yourself</option>`;
  card.innerHTML = `
    <h2>Compose a new pin</h2>
    <span class="sarathi-secondary-label">Send a home-screen button to your parent</span>
    <form id="sarathi-pin-form" style="display:flex;flex-direction:column;gap:18px;margin-top:20px">
      <label style="font-size:18px;font-weight:700">From <select name="from" style="display:block;width:100%;min-height:60px;font-size:18px;border:2px solid var(--sarathi-rule);border-radius:12px;padding:10px;font-family:inherit">${senderOptions}</select></label>
      <label style="font-size:18px;font-weight:700">Label <input name="label" required maxlength="28" placeholder="e.g. Pay Maid ₹3000" style="display:block;width:100%;min-height:60px;font-size:20px;border:2px solid var(--sarathi-rule);border-radius:12px;padding:12px;font-family:inherit"></label>
      <label style="font-size:18px;font-weight:700">Icon (1-2 chars) <input name="icon" required maxlength="4" placeholder="💰" style="display:block;width:140px;min-height:60px;font-size:32px;text-align:center;border:2px solid var(--sarathi-rule);border-radius:12px;padding:8px;font-family:inherit"></label>
      <label style="font-size:18px;font-weight:700">Action <input name="actionUrl" required placeholder="https://wa.me/91... or upi://pay?pa=..." style="display:block;width:100%;min-height:60px;font-size:18px;border:2px solid var(--sarathi-rule);border-radius:12px;padding:12px;font-family:inherit"></label>
      <p style="font-size:14px;color:var(--sarathi-ash);margin:0">Allowed: https, tel, mailto, sarathi, upi. Other schemes are rejected for safety.</p>
      <div class="sarathi-row">
        <button type="submit" style="min-height:88px;background:var(--sarathi-saffron);color:#fff;border:3px solid var(--sarathi-saffron-dark);border-radius:14px;font-size:22px;font-weight:700;cursor:pointer">Send pin</button>
        <button type="button" id="sarathi-pin-cancel" style="min-height:88px;background:transparent;color:var(--sarathi-ink);border:3px solid var(--sarathi-ink);border-radius:14px;font-size:22px;font-weight:700;cursor:pointer">Cancel</button>
      </div>
      <p id="sarathi-pin-msg" role="status" style="font-size:17px;color:var(--sarathi-success);margin:0"></p>
    </form>
  `;
  const form = card.querySelector<HTMLFormElement>("#sarathi-pin-form");
  const cancel = card.querySelector<HTMLButtonElement>("#sarathi-pin-cancel");
  const msg = card.querySelector<HTMLElement>("#sarathi-pin-msg");
  cancel?.addEventListener("click", () => navigate("pinned"));
  form?.addEventListener("submit", (e) => {
    e.preventDefault();
    const fd = new FormData(form);
    try {
      const fromVal = String(fd.get("from") ?? "self");
      const fromContact = family.find((f) => f.phone === fromVal);
      enqueuePin({
        label: String(fd.get("label") ?? "").slice(0, 28),
        icon: String(fd.get("icon") ?? "★").slice(0, 4),
        actionUrl: String(fd.get("actionUrl") ?? ""),
        fromLabel: fromContact?.name ?? "Family",
        fromContactId: fromContact?.phone ?? "self",
      });
      if (msg) msg.textContent = `✓ Pin sent! ${makeBilingual(i18n, "pin.new_request", lang).primary}`;
      form.reset();
    } catch (err) {
      if (msg) {
        msg.style.color = "var(--sarathi-danger)";
        msg.textContent = `× ${(err as Error).message}`;
      }
    }
  });
  return card;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    const map: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
    return map[c] ?? c;
  });
}
